import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import {
  FileText, CheckCircle2, AlertCircle, Sparkles, ArrowUpRight, Users, Layers,
  TrendingUp, Lightbulb, Wand2, BarChart3, Calendar, Building2, Send, Briefcase,
  Award, Activity, Target, Crown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart as RPieChart, Pie, Cell, RadialBarChart, RadialBar, Legend, LineChart, Line,
} from "recharts";

const PALETTE = ["#1E5BFF", "#7C3AED", "#10B981", "#F59E0B", "#EF4444", "#06B6D4", "#EC4899", "#8B5CF6"];
const PIPE_COLOURS = {
  draft: "#64748B", ready: "#0EA5E9", submitted: "#6366F1", interview: "#8B5CF6",
  offer: "#F97316", placed: "#10B981", hold: "#F59E0B", rejected: "#F43F5E",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [insights, setInsights] = useState(null);
  const [openJobs, setOpenJobs] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/metrics").then((r) => setM(r.data));
    api.get("/job-profiles").then((r) => setOpenJobs((r.data || []).filter((j) => j.status !== "closed").slice(0, 6))).catch(() => {});
    api.get("/dashboard/insights").then((r) => setInsights(r.data));
    api.get("/interviews?status=scheduled").then((r) => setInterviews((r.data || []).slice(0, 4))).catch(() => {});
  }, []);

  const pipeline = [
    { key: "draft", label: "Draft" }, { key: "ready", label: "Ready" },
    { key: "submitted", label: "Submitted" }, { key: "interview", label: "Interview" },
    { key: "offer", label: "Offer" }, { key: "placed", label: "Placed" },
    { key: "hold", label: "Hold" }, { key: "rejected", label: "Rejected" },
  ].map((s) => ({ ...s, count: (m?.pipeline_counts || {})[s.key] || 0, color: PIPE_COLOURS[s.key] }));
  const pipelineMax = Math.max(1, ...pipeline.map((p) => p.count));

  const expBreakdown = m?.experience_breakdown || { fresher: 0, experienced: 0, unspecified: 0 };
  const expPie = [
    { name: "Fresher", value: expBreakdown.fresher, color: "#06B6D4" },
    { name: "Experienced", value: expBreakdown.experienced, color: "#1E5BFF" },
    { name: "Unspecified", value: expBreakdown.unspecified, color: "#94A3B8" },
  ].filter((d) => d.value > 0);

  const hrCounts = m?.hr_status_counts || {};
  const hrBars = [
    { name: "Pending", v: hrCounts.pending || 0, color: "#94A3B8" },
    { name: "Shortlisted", v: hrCounts.shortlisted || 0, color: "#10B981" },
    { name: "Hold", v: hrCounts.hold || 0, color: "#F59E0B" },
    { name: "Rejected", v: hrCounts.rejected || 0, color: "#F43F5E" },
  ];
  const ivCounts = m?.interviewer_status_counts || {};
  const ivBars = [
    { name: "Pending", v: ivCounts.pending || 0, color: "#94A3B8" },
    { name: "Selected", v: ivCounts.selected || 0, color: "#10B981" },
    { name: "Hold", v: ivCounts.hold || 0, color: "#F59E0B" },
    { name: "Rejected", v: ivCounts.rejected || 0, color: "#F43F5E" },
  ];

  const quota = m?.quota;
  const quotaPct = quota && quota.max > 0 ? Math.min(100, Math.round((quota.used / quota.max) * 100)) : 0;
  const quotaUnlimited = !quota || quota.max === 0;

  return (
    <div className="p-6 md:p-10 max-w-[1400px] mx-auto" data-testid="dashboard">
      {/* HERO */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-primary/15 via-card to-card p-6 md:p-8 mb-6">
        <div className="absolute -top-12 -right-12 size-64 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 size-72 rounded-full bg-accent/10 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold flex items-center gap-2">
              <Sparkles className="size-3.5 text-primary" /> Mission Control
            </div>
            <h1 className="display text-3xl md:text-5xl font-black tracking-tighter mt-2">
              Welcome back, <span className="bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">{user?.full_name?.split(" ")[0] || "Recruiter"}</span>
            </h1>
            <p className="text-muted-foreground mt-2 text-sm">Pipeline, performance and AI insights at a glance.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate("/builder")} className="lift gap-2" data-testid="dashboard-cta-builder">
              <Wand2 className="size-4" /> Build from scratch
            </Button>
            <Button onClick={() => navigate("/upload")} className="lift gap-2 shadow-lg shadow-primary/20" data-testid="dashboard-cta-upload">
              <Sparkles className="size-4" /> Standardize a resume <ArrowUpRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* TOP TILE GRID */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <StatTile label="Total candidates" value={m?.total} delta={m?.new_this_week} deltaLabel={`+${m?.new_this_week || 0} this wk`} icon={FileText} accent="#1E5BFF" onClick={() => navigate("/organizer")} testid="metric-total" />
        <StatTile label="Open requisitions" value={m?.open_jobs_count} icon={Briefcase} accent="#7C3AED" onClick={() => navigate("/job-profiles")} testid="metric-open-jobs" />
        <StatTile label="Interviews scheduled" value={m?.interviews_scheduled} icon={Calendar} accent="#0EA5E9" onClick={() => navigate("/interviews")} testid="metric-interviews" />
        <StatTile label="Clients" value={m?.clients_count} icon={Building2} accent="#10B981" onClick={() => navigate("/clients")} testid="metric-clients" />
      </div>

      {/* HERO ROW: usage donut + 14-day area chart + pipeline funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-4">
        {/* Quota donut */}
        <Card className="border-border lg:col-span-3" data-testid="quota-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2">
              <div className="size-7 rounded-md grid place-items-center" style={{ background: (quota?.plan?.color || "#10B981") + "22", color: quota?.plan?.color || "#10B981" }}>
                <Crown className="size-4" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Plan</div>
                <div className="display font-bold text-sm">{quota?.plan?.name || "—"}</div>
              </div>
            </div>
            <div className="mt-4 relative">
              {quotaUnlimited ? (
                <div className="h-44 grid place-items-center text-center">
                  <div>
                    <div className="display text-4xl font-extrabold">{quota?.used ?? 0}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mt-1">Active candidates</div>
                    <div className="text-[10px] mt-2 px-2 py-1 inline-flex items-center rounded-full bg-emerald-500/15 text-emerald-700 font-semibold">UNLIMITED</div>
                  </div>
                </div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={170}>
                    <RadialBarChart innerRadius="68%" outerRadius="100%" data={[{ name: "used", v: quotaPct, fill: quota?.plan?.color || "#1E5BFF" }]} startAngle={90} endAngle={-270}>
                      <RadialBar background={{ fill: "hsl(var(--secondary))" }} dataKey="v" cornerRadius={20} />
                    </RadialBarChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 grid place-items-center pointer-events-none">
                    <div className="text-center">
                      <div className="display text-3xl font-extrabold">{quota?.used ?? 0}<span className="text-base font-medium text-muted-foreground"> / {quota?.max ?? 0}</span></div>
                      <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mt-0.5">Used</div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground mt-2 text-center">Counts ready / submitted / interview / offer / placed / hold</div>
          </CardContent>
        </Card>

        {/* 14-day area chart */}
        <Card className="border-border lg:col-span-6" data-testid="area-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Activity</div>
                <div className="display font-bold text-lg">Candidates added · last 14 days</div>
              </div>
              <Activity className="size-4 text-primary" />
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={m?.created_14d || []} margin={{ left: -20, right: 0, top: 10, bottom: 0 }}>
                <defs>
                  <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1E5BFF" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#1E5BFF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="count" stroke="#1E5BFF" strokeWidth={2} fill="url(#areaGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Fresher vs Experienced pie */}
        <Card className="border-border lg:col-span-3" data-testid="exp-pie-card">
          <CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Experience mix</div>
            <div className="display font-bold text-base mb-2">Fresher vs experienced</div>
            {expPie.length === 0 ? (
              <div className="h-44 grid place-items-center text-sm text-muted-foreground">No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={160}>
                <RPieChart>
                  <Pie data={expPie} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>
                    {expPie.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                </RPieChart>
              </ResponsiveContainer>
            )}
            <div className="space-y-1 mt-1">
              {expPie.map((d) => (
                <div key={d.name} className="flex items-center justify-between text-[11px]">
                  <span className="flex items-center gap-1.5"><span className="size-2 rounded-full" style={{ background: d.color }} /> {d.name}</span>
                  <span className="font-semibold">{d.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* PIPELINE FUNNEL (horizontal bars) */}
      <Card className="border-border mb-4" data-testid="pipeline-funnel">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Pipeline funnel</div>
              <div className="display font-bold text-lg">Recruitment stages</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate("/organizer")} className="gap-1" data-testid="dash-view-pipeline">
              Pipeline view <ArrowUpRight className="size-3.5" />
            </Button>
          </div>
          <div className="space-y-2">
            {pipeline.map((p) => {
              const pct = (p.count / pipelineMax) * 100;
              return (
                <button key={p.key} onClick={() => navigate("/organizer")} className="w-full text-left lift block group" data-testid={`dash-pipe-${p.key}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-24 text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground group-hover:text-foreground transition-colors">{p.label}</div>
                    <div className="flex-1 h-7 rounded-md bg-secondary overflow-hidden relative">
                      <div className="absolute inset-y-0 left-0 rounded-md transition-all" style={{ width: `${Math.max(4, pct)}%`, background: `linear-gradient(90deg, ${p.color} 0%, ${p.color}cc 100%)` }} />
                      <div className="absolute inset-0 flex items-center justify-end pr-3 font-bold text-xs text-foreground">
                        {p.count}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* HR + Interviewer feedback charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <Card className="border-border" data-testid="hr-chart">
          <CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">HR feedback</div>
            <div className="display font-bold text-lg mb-2">Status distribution</div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={hrBars}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="v" radius={[6, 6, 0, 0]}>
                  {hrBars.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="border-border" data-testid="iv-chart">
          <CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Interviewer feedback</div>
            <div className="display font-bold text-lg mb-2">Status distribution</div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={ivBars}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="v" radius={[6, 6, 0, 0]}>
                  {ivBars.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Open requisitions + Upcoming interviews + Top skills */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Card className="border-border lg:col-span-2" data-testid="open-reqs-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="size-7 rounded-md bg-primary/10 text-primary grid place-items-center"><Briefcase className="size-4" /></div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Open requisitions</div>
                  <div className="display font-bold text-sm leading-none mt-1">Job descriptions you're hiring for</div>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => navigate("/job-profiles")} className="gap-1" data-testid="dash-view-all-jobs">View all <ArrowUpRight className="size-3.5" /></Button>
            </div>
            {openJobs.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">No open requisitions — <button onClick={() => navigate("/job-profiles")} className="text-primary underline">create one</button>.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="dash-open-jobs">
                {openJobs.map((j) => (
                  <button key={j.id} onClick={() => navigate("/job-profiles")} className="text-left rounded-lg border border-border bg-card p-3 lift hover:border-primary/40" data-testid={`dash-job-${j.id}`}>
                    <div className="font-bold truncate flex items-center gap-1.5 text-sm">
                      {j.public && <span className="size-1.5 rounded-full bg-emerald-500" title="Public" />}
                      {j.title}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{j.seniority || "—"} · {j.min_years_experience || 0}+ yrs · {j.location || "Remote"}</div>
                    {j.required_skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {j.required_skills.slice(0, 3).map((s) => <span key={s} className="chip bg-secondary text-foreground text-[10px]">{s}</span>)}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border" data-testid="upcoming-interviews-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="size-7 rounded-md bg-purple-500/15 text-purple-600 grid place-items-center"><Calendar className="size-4" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Upcoming</div>
                <div className="display font-bold text-sm leading-none mt-1">Interviews</div>
              </div>
            </div>
            {interviews.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">No scheduled interviews.</div>
            ) : (
              <div className="space-y-2">
                {interviews.map((iv) => (
                  <button key={iv.id} onClick={() => navigate("/interviews")} className="w-full text-left rounded-md border border-border p-2 lift hover:border-primary/40" data-testid={`dash-iv-${iv.id}`}>
                    <div className="font-bold text-xs truncate">{iv.candidate_name || "Candidate"}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{iv.scheduled_at ? new Date(iv.scheduled_at).toLocaleString() : "—"} · {iv.mode}</div>
                  </button>
                ))}
              </div>
            )}
            <Button variant="outline" size="sm" className="w-full mt-3 gap-1" onClick={() => navigate("/interviews")}>
              <Plus /> Schedule
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Top skills */}
        <Card className="lg:col-span-2 border-border">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Top skills</div>
                <div className="display font-bold text-lg">Aggregated across library</div>
              </div>
              <BarChart3 className="size-4 text-muted-foreground" />
            </div>
            {(insights?.top_skills || []).length === 0 ? (
              <div className="text-sm text-muted-foreground py-4">No data yet — upload resumes to populate insights.</div>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(220, 28 * (insights?.top_skills?.length || 8))}>
                <BarChart data={(insights?.top_skills || []).slice(0, 10)} layout="vertical" margin={{ left: 0, right: 30, top: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: "hsl(var(--foreground))" }} axisLine={false} tickLine={false} width={140} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                    {(insights?.top_skills || []).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* AI insights */}
        <Card className="border-border bg-gradient-to-br from-primary/10 to-transparent">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="size-7 rounded-md bg-primary text-primary-foreground grid place-items-center"><Lightbulb className="size-4" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">AI insights</div>
                <div className="display font-bold text-sm leading-none mt-1">From your library</div>
              </div>
            </div>
            {(insights?.ai_bullets || []).length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">No insights yet.</div>
            ) : (
              <div className="space-y-2">
                {insights.ai_bullets.map((b, i) => (
                  <div key={i} className="border border-border rounded-md p-3 bg-card text-sm flex items-start gap-2">
                    <Sparkles className="size-3.5 text-primary mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent + Top contributors */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-border lg:col-span-2">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Recent activity</div>
                <div className="display font-bold text-lg">Latest resumes</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate("/organizer")} data-testid="dashboard-view-all">View all</Button>
            </div>
            <div className="divide-y divide-border">
              {(m?.recent || []).length === 0 && (
                <div className="text-sm text-muted-foreground py-8 text-center">No resumes yet — upload your first one.</div>
              )}
              {(m?.recent || []).map((r) => (
                <button key={r.id} onClick={() => navigate(`/editor/${r.id}`)} data-testid={`recent-${r.id}`} className="w-full flex items-center justify-between py-3 lift hover:bg-secondary px-2 rounded-md text-left">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="size-9 grid place-items-center rounded-md" style={{ background: (PIPE_COLOURS[r.pipeline_status] || "#94A3B8") + "22", color: PIPE_COLOURS[r.pipeline_status] || "#94A3B8" }}>
                      <FileText className="size-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold truncate text-sm">{r.data?.name || r.title}</div>
                      <div className="text-xs text-muted-foreground truncate">{r.data?.title || "—"} · <span className="uppercase tracking-wide font-semibold" style={{ color: PIPE_COLOURS[r.pipeline_status] }}>{r.pipeline_status || "draft"}</span></div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">{new Date(r.updated_at).toLocaleDateString()}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Users className="size-4 text-primary" />
              <div className="display font-bold text-lg">Top contributors</div>
            </div>
            {(m?.top_recruiters || []).length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">No contributors yet.</div>
            ) : (
              <div className="space-y-2">
                {m.top_recruiters.slice(0, 6).map((u, i) => (
                  <div key={u.email} className="border border-border rounded-md p-2.5 flex items-center justify-between lift">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="size-8 grid place-items-center rounded-full text-white font-bold text-xs" style={{ background: PALETTE[i % PALETTE.length] }}>{(u.name || "?")[0]}</div>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{u.name}</div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{u.role.replace("_", " ")}</div>
                      </div>
                    </div>
                    <div className="display font-extrabold">{u.count}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatTile({ label, value, delta, deltaLabel, icon: Icon, accent, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="text-left rounded-xl border border-border bg-card p-5 lift hover:border-primary/40 w-full overflow-hidden relative group">
      <div className="absolute -top-6 -right-6 size-20 rounded-full opacity-10 group-hover:opacity-20 transition-opacity" style={{ background: accent }} />
      <div className="relative flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">{label}</div>
        <div className="size-8 grid place-items-center rounded-md" style={{ background: accent + "22", color: accent }}>
          <Icon className="size-4" />
        </div>
      </div>
      <div className="relative mt-3 display text-3xl font-extrabold tracking-tight">{value ?? 0}</div>
      {deltaLabel != null && (
        <div className="relative mt-1 inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5" style={{ background: accent + "1A", color: accent }}>
          <TrendingUp className="size-3" /> {deltaLabel}
        </div>
      )}
    </button>
  );
}

// Tiny inline Plus to avoid extra import
function Plus() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>; }
